import { RefreshCw } from 'lucide-react'
import React, { useRef, useState, useEffect, memo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import styles from '~/styles/NodeDocument.module.css'
import MarkdownRenderer from '../markdown-renderer/MarkdownRenderer'
import PrintingTab from './PrintingTab'

// Utility function to handle scrolling
const handleScroll = (element, deltaY) => {
  if (!element) return

  const { scrollTop, scrollHeight, clientHeight } = element
  const isAtTop = scrollTop <= 0
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2
  const isScrollingUp = deltaY < 0
  const isScrollingDown = deltaY > 0

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY
    return true // Event was handled
  }
  return false // Let it bubble for panning
}

// Update the interface
interface DocumentTabsProps {
  contentMarkdown: string
  onContentChange: (content: string) => void
  nodeId: string
  context: string
  onContextChange: (context: string) => void
  linkedKnowledge: string
  isMarkdownExpanded: boolean
  markdownHeight: string
  textareaHeight: string
  nodebaseHeight: string
  markdownFormatVisible: boolean
  onSectionSelect: (
    sectionId: string,
    sectionName: string,
    event: React.MouseEvent
  ) => void
  sectionStates: Record<string, "none" | "accepted" | "rejected" | "pending">
  processingStates?: Record<string, boolean>
  sectionReviews?: Record<string, {
    originalContent: string
    suggestedContent: string
    commandType: string
  }>
  onAcceptReview?: (sectionId: string) => void
  onRejectReview?: (sectionId: string) => void
  markdownPreviewRef: React.RefObject<HTMLDivElement>
  onRefreshLinkedKnowledge: () => void
  onLinkedKnowledgeChange: (linkedKnowledge: string) => void
  researchTopic?: string
  onResearchTopicChange?: (topic: string) => void
  protectedSections?: Record<string, boolean>
  sectionExplanations?: Record<string, string>
  sectionExplanationStates?: Record<string, boolean>
  onToggleExplanation?: (sectionId: string) => void
  printSettings?: {
    headerText: string
    footerText: string
    coverMarkdown: string
  }
  onPrintSettingsChange?: (settings: { headerText: string; footerText: string; coverMarkdown: string }) => void
  data?: {
    label?: string
    assignedEmails?: string[]
  }
}

const LinkedKnowledgeTab = React.memo(
  ({
    linkedKnowledge,
    onRefreshLinkedKnowledge,
    nodebaseHeight,
    isMarkdownExpanded,
  }: {
    linkedKnowledge: string
    onRefreshLinkedKnowledge: () => void
    nodebaseHeight: string
    isMarkdownExpanded: boolean
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const container = scrollContainerRef.current
      if (!container) return

      const handleWheelEvent = (e: WheelEvent) => {
        if (handleScroll(container, e.deltaY)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      container.addEventListener('wheel', handleWheelEvent, { passive: false })
      return () => {
        container.removeEventListener('wheel', handleWheelEvent)
      }
    }, [])

    return (
      <div className="p-3 h-full">
        <div className="flex justify-between mb-2">
          <div className="text-sm text-gray-500">
            This panel shows knowledge linked from other nodes.
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onRefreshLinkedKnowledge()
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div
          ref={scrollContainerRef}
          className="overflow-auto markdown-body p-2 border rounded h-auto scrollable-content"
          style={{
            height: isMarkdownExpanded ? 'auto' : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            border: '1px solid #ccc',
            padding: '8px',
            overflowY: 'auto',
          }}
        >
          {linkedKnowledge ? (
            <div className={`${styles.markdownPreview} scrollable-content`}>
              <MarkdownRenderer
                markdown={`\`\`\`markdown\n${linkedKnowledge}\n\`\`\``}
              />
            </div>
          ) : (
            <div className="text-gray-400 italic p-2">
              No linked knowledge available. Connect this document to other
              nodes using the Data Settings.
            </div>
          )}
        </div>
      </div>
    )
  }
)

const DocumentTabs: React.FC<DocumentTabsProps> = ({
  contentMarkdown,
  onContentChange,
  nodeId,
  context,
  onContextChange,
  linkedKnowledge,
  isMarkdownExpanded,
  markdownHeight,
  textareaHeight,
  nodebaseHeight,
  markdownFormatVisible,
  onSectionSelect,
  sectionStates,
  processingStates = {},
  sectionReviews = {},
  onAcceptReview,
  onRejectReview,
  markdownPreviewRef,
  onRefreshLinkedKnowledge,
  onLinkedKnowledgeChange,
  researchTopic = '',
  onResearchTopicChange,
  protectedSections = {},
  sectionExplanations = {},
  sectionExplanationStates = {},
  onToggleExplanation,
  printSettings = { headerText: '', footerText: '', coverMarkdown: '' },
  onPrintSettingsChange,
  data,
}) => {
  const [activeTab, setActiveTab] = useState('document')
  const contentRefs = useRef<{
    document: HTMLDivElement | null
    markdown: HTMLDivElement | null
    markdownEditable: HTMLTextAreaElement | null
    context: HTMLTextAreaElement | null
    researchTopic: HTMLTextAreaElement | null
    linkedKnowledge: HTMLDivElement | null
  }>({
    document: null,
    markdown: null,
    markdownEditable: null,
    context: null,
    researchTopic: null,
    linkedKnowledge: null,
  })

  const [localContext, setLocalContext] = useState(context)
  const [localResearchTopic, setLocalResearchTopic] = useState(researchTopic)
  const [localLinkedKnowledge, setLocalLinkedKnowledge] = useState(linkedKnowledge)
  const [localContent, setLocalContent] = useState(contentMarkdown) // Restore localContent state for markdown editable

  // Sync local state with props
  useEffect(() => {
    setLocalContext(context)
    if (researchTopic !== undefined) setLocalResearchTopic(researchTopic)
    setLocalLinkedKnowledge(linkedKnowledge)
    setLocalContent(contentMarkdown)
  }, [contentMarkdown, context, linkedKnowledge, researchTopic])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
  }

  // Wheel event listener management with retry mechanism
  useEffect(() => {
    const attachListeners = () => {
      const handleWheel = (e: WheelEvent) => {
        const target = e.currentTarget as HTMLElement | HTMLTextAreaElement
        if (handleScroll(target, e.deltaY)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      const elements = Object.entries(contentRefs.current)
        .filter(([_, el]) => el !== null)
        .map(([_, el]) => el) as (HTMLDivElement | HTMLTextAreaElement)[]      // Log for debugging
      console.log(
        'DocumentTabs: Attaching scroll listeners to elements:',
        elements.map((el) => ({
          tagName: el.tagName,
          className: el.className,
          id: el.id || 'no-id',
          isConnected: el.isConnected,
          offsetParent: el.offsetParent?.tagName || 'null'
        }))
      )

      elements.forEach((el) => {
        el.addEventListener('wheel', handleWheel, { passive: false })
      })

      return () => {
        elements.forEach((el) => {
          el.removeEventListener('wheel', handleWheel)
        })
      }
    }

    // Initial attachment
    let cleanup = attachListeners()

    // Retry after a short delay to catch late-mounted elements
    const timeout = setTimeout(() => {
      cleanup()
      cleanup = attachListeners()
    }, 100) // 100ms delay to ensure DOM is settled

    return () => {
      clearTimeout(timeout)
      cleanup()
    }
  }, [activeTab]) // Trigger on tab change

  const handleContextBlur = () => {
    if (localContext !== context) onContextChange(localContext)
  }

  const handleResearchTopicBlur = () => {
    if (onResearchTopicChange && localResearchTopic !== researchTopic) {
      onResearchTopicChange(localResearchTopic)
    }
  }

  const handleLinkedKnowledgeBlur = () => {
    if (onLinkedKnowledgeChange && localLinkedKnowledge !== linkedKnowledge) {
      onLinkedKnowledgeChange(localLinkedKnowledge)
    }
  }

  // Only sync on blur
  const handleContentBlur = () => {
    if (localContent !== contentMarkdown) onContentChange(localContent)
  }

  // Create a callback ref for the markdown preview
  const setMarkdownPreviewRef = useCallback((el: HTMLDivElement | null) => {
    if (markdownPreviewRef && el) {
      (markdownPreviewRef as React.MutableRefObject<HTMLDivElement | null>).current = el
    }
    contentRefs.current.document = el
  }, [markdownPreviewRef])

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full ">
      <TabsList className={`grid w-full grid-flow-col h-12`}>
        <TabsTrigger value="document" className={`${styles.documentTabsTitle}`}>Document</TabsTrigger>
        {markdownFormatVisible && (
          <TabsTrigger value="markdown" className={`${styles.documentTabsTitle}`}>Markdown</TabsTrigger>
        )}
        <TabsTrigger value="markdownEditable" className={`${styles.documentTabsTitle}`}>
          Markdown
        </TabsTrigger>
        <TabsTrigger value="context" className={`${styles.documentTabsTitle}`}>Context</TabsTrigger>
        {onResearchTopicChange && (
          <TabsTrigger value="researchTopic" className={`${styles.documentTabsTitle}`}>Research Topic</TabsTrigger>
        )}
        <TabsTrigger value="linkedKnowledge" className={`${styles.documentTabsTitle}`}>Linked Knowledge</TabsTrigger>
        <TabsTrigger value="printable" className={`${styles.documentTabsTitle}`}>Printable</TabsTrigger>
      </TabsList>
      <TabsContent value="document">
        <div
          ref={setMarkdownPreviewRef}
          className={`${styles.markdownPreview} scrollable-content`}
          style={{
            height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            border: '1px solid #ccc',
            padding: '8px',
            overflowY: 'auto',
          }}
        >
          {' '}
          <MarkdownRenderer
            markdown={contentMarkdown.trim()}
            nodeId={nodeId}
            onSectionSelect={onSectionSelect}
            sectionStates={sectionStates}
            processingStates={processingStates}
            sectionReviews={sectionReviews}
            onAcceptReview={onAcceptReview}
            onRejectReview={onRejectReview}
            protectedSections={protectedSections}
            sectionExplanations={sectionExplanations}
            sectionExplanationStates={sectionExplanationStates}
            onToggleExplanation={onToggleExplanation}
          />
        </div>
      </TabsContent>
      {markdownFormatVisible && (
        <TabsContent value="markdown">
          <div
            ref={(el) => (contentRefs.current.markdown = el)}
            className={`${styles.markdownPreview} scrollable-content`}
            style={{
              height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
              maxHeight: isMarkdownExpanded ? 'none' : 'auto',
              border: '1px solid #ccc',
              padding: '8px',
              overflowY: 'auto',
            }}
          >
            {' '}
            <MarkdownRenderer
              markdown={`\`\`\`markdown\n${contentMarkdown.trim()}\n\`\`\``}
              sectionStates={sectionStates}
              processingStates={processingStates}
              sectionExplanations={sectionExplanations}
              sectionExplanationStates={sectionExplanationStates}
              onToggleExplanation={onToggleExplanation}
            />
          </div>
        </TabsContent>
      )}
      <TabsContent value="markdownEditable">
        <Textarea
          ref={(el) => (contentRefs.current.markdownEditable = el)}
          className="textArea nodrag scrollable-content"
          placeholder="Content Markdown: Enter the document content."
          value={localContent}
          onChange={(e) => setLocalContent(e.target.value)}
          onBlur={handleContentBlur}
          style={{
            width: '100%',
            height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
          }}
        />
      </TabsContent>
      <TabsContent value="context">
        <Textarea
          ref={(el) => (contentRefs.current.context = el)}
          className="textArea nodrag scrollable-content"
          placeholder="Enter document context information here"
          value={localContext}
          onChange={(e) => setLocalContext(e.target.value)}
          onBlur={handleContextBlur}
          style={{
            width: '100%',
            height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
          }}
        />
      </TabsContent>
      {onResearchTopicChange && (
        <TabsContent value="researchTopic">
          <Textarea
            ref={(el) => (contentRefs.current.researchTopic = el)}
            className="textArea nodrag scrollable-content"
            placeholder="Enter research topic"
            value={localResearchTopic}
            onChange={(e) => setLocalResearchTopic(e.target.value)}
            onBlur={handleResearchTopicBlur}
            style={{
              width: '100%',
              height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
              maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            }}
          />
        </TabsContent>
      )}
      <TabsContent value="linkedKnowledge">
        <LinkedKnowledgeTab
          linkedKnowledge={linkedKnowledge}
          onRefreshLinkedKnowledge={onRefreshLinkedKnowledge}
          nodebaseHeight={nodebaseHeight}
          isMarkdownExpanded={isMarkdownExpanded}
        />
      </TabsContent>
      <TabsContent value="printable">
        <PrintingTab
          contentMarkdown={contentMarkdown}
          isMarkdownExpanded={isMarkdownExpanded}
          markdownHeight={markdownHeight}
          nodebaseHeight={nodebaseHeight}
          printSettings={printSettings}
          onPrintSettingsChange={onPrintSettingsChange}
          data={data}
        />
      </TabsContent>
    </Tabs>
  )
}

export default memo(DocumentTabs)
